import type {
  RateLimitationSettings,
  RateLimitationSettingsWithMultipleRateLimiters,
  RateLimiterSettings,
} from '../types';
import type {
  BaseVendorApiSettingsDefaults,
  HttpAgentSettingsDefaults,
  SessionSettingsDefaults,
  VendorApiHttpAdvancedSettingsDefaults,
  WebsocketParsingOptionsDefaults,
  WebsocketTimeoutsDefaults,
} from './types';

export const defaultHttpAgentSettings: HttpAgentSettingsDefaults = {
  keepAlive: true,
  keepAliveMs: 1000,
  enableLegacyTlsRenegotiation: true,
};

export const defaultVendorApiHttpAdvancedSettings: VendorApiHttpAdvancedSettingsDefaults =
  {
    maxAttemptsOnRecoverableNetworkError: 1,
    requestTimeoutMs: 3 * 60 * 1000,
    serverOverloadedIntervalMs: 10_000,
    serverOverloadedMaxCount: 10,
    serverOverloadedMaxRequestAttempts: 20,
    serverOverloadedMaxWaitTimeMs: 5 * 60 * 1000,
    tooManyRequestsWaitTimeMs: null,
    tooManyRequestsWaitTimeRandomSpreadMs: 5000,
  };

export const defaultSessionSettings: SessionSettingsDefaults = {
  defaultSessionTimeoutSec: 600,
  failedSessionRefreshCountMax: 3,
  failedSessionRefreshWaitTimeBaseSec: 2,
  failedSessionRefreshWaitTimeMaxSec: 120,
  sessionRefreshSafetyFactorPct: 80,
};

/**
 * How many requests for the controller are tried the most in parallel.
 */
export const maxConcurrentRequests = {
  defaultValue: 100_000,
  boundaryValues: {
    min: 1,
    max: 1_000_000,
  },
};

/**
 * The exponential backoff is applied when a session refresh fails.
 */
export const refillRate = {
  defaultValue: 10,
  boundaryValues: {
    min: 1,
    max: 1_000_000,
  },
};

/**
 * Maximum number of burst request sent at the beginning of discovery
 */
export const maxCapacity = {
  defaultValue: 300,
  boundaryValues: {
    min: 1,
    max: 1_000_000,
  },
};

/**
 * The refillRateIntervalMs value with refillRate value creates a rate-limiting (number of requests per time period).
 */
export const refillRateIntervalMs = {
  defaultValue: 1000,
  boundaryValues: {
    min: 500,
    max: 24 * 60 * 60 * 100,
  },
};

const defaultRateLimiterSettings: RateLimiterSettings = {
  refillRate: refillRate.defaultValue,
  maxCapacity: maxCapacity.defaultValue,
  refillRateIntervalMs: refillRateIntervalMs.defaultValue,
};

export const defaultPaginationSettings = {
  defaultPageSize: 1000,
};

export const defaultRateLimitationSettings: Required<RateLimitationSettings> = {
  maxConcurrentRequests: maxConcurrentRequests.defaultValue,
  ...defaultRateLimiterSettings,
};

export const baseVendorApiSettingsDefaults: BaseVendorApiSettingsDefaults = {
  clientStatsIntervalSec: 30,
};

export function getDefaultRateLimitationSettings<TRateLimiterId extends string>(
  rateLimiterIds: readonly TRateLimiterId[],
): Required<RateLimitationSettingsWithMultipleRateLimiters<TRateLimiterId>> & {
  rateLimiters: { [id in TRateLimiterId]: Required<RateLimiterSettings> };
} {
  const rateLimiters: {
    [id in TRateLimiterId]: Required<RateLimiterSettings>;
  } = {} as { [id in TRateLimiterId]: Required<RateLimiterSettings> };
  for (const rateLimiterId of rateLimiterIds) {
    rateLimiters[rateLimiterId] = { ...defaultRateLimiterSettings };
  }
  return {
    maxConcurrentRequests: maxConcurrentRequests.defaultValue,
    rateLimiters,
  };
}

export const defaultWebsocketParsingOptions: WebsocketParsingOptionsDefaults = {
  wsParseIncomingData: true,
};

export const defaultWebsocketTimeouts: WebsocketTimeoutsDefaults = {
  wsAwaitedMessageTimeoutSec: 3 * 60, // the same as VendorApiHttpAdvancedSettingsDefaults.requestTimeoutMs
  wsConnectTimeoutSec: 3 * 60, // the same as VendorApiHttpAdvancedSettingsDefaults.requestTimeoutMs
  wsPingIntervalSec: 120,
  wsSendMessageTimeoutSec: 3 * 60, // the same as VendorApiHttpAdvancedSettingsDefaults.requestTimeoutMs
  wsIdleMaxSec: 10 * 60,
};
